import {
  EUserType,
  ITurmaObs,
  schemaTurmaObs,
  utils,
  yupTurmaObs,
} from "@deltasge/marauders-map";
import { Optional } from "@deltasge/marauders-map/lib/utils";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  LinearProgress,
  MenuItem,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { snack } from "components/GlobalSnackbar";
import { api } from "configs";
import { format, parseISO } from "date-fns";
import { useApplyProps } from "hooks/useApplyProps";
import React, { FC, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useAppSelector } from "store";
import { getError } from "utils";

type ITurmaObsForm = Omit<
  ITurmaObs,
  "turma" | "curso" | "serie" | "professor" | "materia"
>;

export const CadastroTurmaObs: FC<{
  handleClose: () => void;
  item: Optional<
    Pick<
      ITurmaObs,
      "anoLetivo" | "idCurso" | "idSerie" | "idTurma" | "idMateria" | "data"
    >,
    "data"
  >;
}> = ({ handleClose, item }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const {
    treeView: { cursos },
    usuario: { usuario },
  } = useAppSelector((state) => state);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    watch,
  } = useForm<ITurmaObsForm>({
    defaultValues: {
      ...yupTurmaObs.getDefault(),
      ...item,
      observacao: "",
      data: item.data || format(new Date(), "yyyy-MM-dd"),
    },
    resolver: yupResolver(schemaTurmaObs),
  });

  const id = watch("id");
  const data = watch("data");
  const idMateria = watch("idMateria");
  const idTurma = watch("idTurma");

  const onSubmit = async (data: ITurmaObsForm) => {
    try {
      setSaving(true);
      const { id, ...payload } = data;
      if (id) {
        await api.put(`observacao-turma/${id}`, payload);
      } else {
        await api.post("observacao-turma", payload);
      }
      handleClose();
      snack.success("Observação salva com sucesso!");
    } catch (error) {
      snack.error(getError(error));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    try {
      setDeleting(true);
      if (!id) {
        throw new Error("A observação não pode ser excluida");
      }
      await api.delete(`observacao-turma/${id}`);
      handleClose();
    } catch (error) {
      snack.error(getError(error));
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const {
          data: [obs],
        } = await api.get<[ITurmaObs[]]>("observacao-turma", {
          cache: { ignoreCache: true },
          params: {
            where: {
              anoLetivo: item.anoLetivo,
              idTurma,
              idMateria,
              data,
            },
          },
        });
        let turmaObs: ITurmaObs = {
          ...yupTurmaObs.getDefault(),
          ...item,
          observacao: "",
          data,
          idMateria,
          id: undefined,
        };
        if (Array.isArray(obs) && obs.length > 0) {
          turmaObs = obs[0];
        }
        Object.keys(turmaObs).map((key) =>
          setValue(
            key as keyof ITurmaObsForm,
            turmaObs[key as keyof ITurmaObsForm]
          )
        );
      } catch (error) {
        snack.error(getError(error));
      } finally {
        setLoading(false);
      }
    })();
  }, [idTurma, data, idMateria]);

  const applyProps = useApplyProps<ITurmaObs>({
    register,
    errors,
    schema: schemaTurmaObs,
  });

  const turma = utils.getTurmaTreeView({
    cursos,
    idTurma: item.idTurma.toString(),
    idCurso: item.idCurso.toString(),
    idSerie: item.idSerie.toString(),
    type: usuario?.tipoUsuario,
  });

  const materias = utils.getMateriasTreeView({
    turma,
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {loading && <LinearProgress />}
          </Grid>
          <Grid item xs={4} sm={4}>
            <Controller
              control={control}
              name="data"
              render={({ field }) => (
                <DatePicker
                  {...field}
                  disabled={loading}
                  DialogProps={{ fullWidth: false }}
                  label={schemaTurmaObs.fields.data.spec.label}
                  value={parseISO(field.value)}
                  onChange={(data) => {
                    if (data && Date.prototype.isValid(data)) {
                      setValue("data", format(data, "yyyy-MM-dd"));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      helperText={errors.data?.message}
                      error={!!errors.data}
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <Controller
              control={control}
              name="idMateria"
              render={({ field }) => (
                <TextField {...field} select disabled={loading} label="Matéria">
                  {materias.map((materia) => (
                    <MenuItem
                      key={`combo-materia-${materia.id}`}
                      value={materia.id}
                    >
                      {materia.nome?.rUpper()}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Cadastrado por"
              InputProps={{ readOnly: true }}
              defaultValue={
                usuario?.tipoUsuario == EUserType.Professor
                  ? usuario?.nome
                  : "Coordenação"
              }
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              {...applyProps("observacao")}
              multiline
              rows={4}
              label="Observação"
              InputLabelProps={{ shrink: true }}
              disabled={loading}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Fechar
        </Button>
        <LoadingButton
          variant="outlined"
          loading={deleting}
          onClick={onDelete}
          color="secondary"
          disabled={!id}
        >
          Deletar
        </LoadingButton>
        <LoadingButton variant="outlined" loading={saving} type="submit">
          Gravar
        </LoadingButton>
      </DialogActions>
    </form>
  );
};
